import {useCallback, useEffect, useMemo, useState} from 'react';
import {checkInService} from '@/common-pages/check-in/check-in.service';
import {
  ActivityDetailVO,
  ReceiveRewardVO,
  UserDailySignRecordVO,
  CheckInStatus,
  SignInResultVO,
} from '@/common-pages/check-in/types';

// 星期映射
const WEEK_DAY_MAP = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

// 渲染数据类型
export interface RenderDayData {
  day: string;
  reward: string;
  icon: any;
  isChecked: boolean;
  dayOfWeek: number;
  rewardAmount: number;
  arriveStatus?: number;
  receiveStatus?: number;
  status: CheckInStatus;
}

export interface UseCheckInDataReturn {
  // 处理好的渲染数据（可修改签到状态）
  renderList: RenderDayData[];

  // 当前配置原数据
  rawData: ActivityDetailVO | null;

  // 当前星期几
  currentWeekDay: number;

  // 加载状态
  isLoading: boolean;
  isCheckingIn: boolean;
  isReceivingReward: boolean;
  isReceivingExtraReward: boolean;

  // 当天是否已签到
  isTodayChecked: boolean;

  // 额外奖励是否已领取
  extraRewardReceived: boolean;

  // 处理后的每日任务数据
  dailyTasksMap: Map<number, {depositTask: number; betTask: number}>;

  // 当天的任务数据
  currentDayTasks:
    | {
        depositTask: number;
        betTask: number;
        depositTaskCompleted: number;
        betTaskCompleted: number;
      }
    | undefined;

  // 签到方法
  checkIn: () => Promise<SignInResultVO>;

  // 领取奖励方法
  receiveReward: () => Promise<ReceiveRewardVO>;

  // 领取额外奖励方法（宝箱）
  receiveExtraReward: () => Promise<ReceiveRewardVO>;

  // 刷新数据方法
  refreshData: () => Promise<void>;

  // 更新特定天的签到状态
  updateDayStatus: (dayOfWeek: number, checked: boolean) => void;

  // 获取指定状态的数量
  getStatusCount: (status: CheckInStatus) => number;
}

// 奖励图标映射
const rewardIconMap: {[key: string]: any} = {
  reward1: require('@/assets/imgs/check/reward1.webp'),
  reward2: require('@/assets/imgs/check/reward2.webp'),
  reward3: require('@/assets/imgs/check/reward3.webp'),
  reward4: require('@/assets/imgs/check/reward4.webp'),
  reward5: require('@/assets/imgs/check/reward5.webp'),
  reward6: require('@/assets/imgs/check/reward6.webp'),
  reward7: require('@/assets/imgs/check/reward7.webp'),
};

/**
 * 签到数据 Hook
 * 处理签到相关的数据获取、状态管理和业务逻辑
 */
export function useCheckInData(): UseCheckInDataReturn {
  const [rawData, setRawData] = useState<ActivityDetailVO | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [isReceivingReward, setIsReceivingReward] = useState(false);
  const [isReceivingExtraReward, setIsReceivingExtraReward] = useState(false);
  const [checkInDailyTasksMap, setCheckInDailyTasksMap] = useState<
    Map<number, {depositTask: number; betTask: number}>
  >(new Map());

  // 获取当前星期几 (1-7, 1是星期一)
  const currentWeekDay = useMemo(() => {
    return rawData?.indiaDayOfWeek || 1;
  }, [rawData]);

  // 根据星期几动态选择icon
  const getRewardIcon = useCallback((dayOfWeek: number): any => {
    return rewardIconMap[`reward${dayOfWeek}`] || rewardIconMap.reward1;
  }, []);

  // 创建初始空数据
  const createEmptyData = useCallback((): RenderDayData[] => {
    return Array.from({length: 7}, (_, i) => {
      const dayOfWeek = i + 1;
      return {
        day: WEEK_DAY_MAP[dayOfWeek as keyof typeof WEEK_DAY_MAP],
        reward: '0',
        icon: getRewardIcon(i + 1),
        isChecked: false,
        dayOfWeek,
        rewardAmount: 0,
        arriveStatus: 0,
        receiveStatus: 0,
        status: CheckInStatus.LOCKED, // 默认为锁定状态
      };
    });
  }, [getRewardIcon]);

  // 处理签到数据，合并dailyRewards和weekRecords
  const processCheckInData = useCallback(
    (
      data: ActivityDetailVO,
    ): {
      renderList: RenderDayData[];
      dailyTasksMap: Map<number, {depositTask: number; betTask: number}>;
    } => {
      const {dailyRewards = [], weekRecords = [], dailyTasks} = data;
      const nextDailyTasksMap = new Map<
        number,
        {depositTask: number; betTask: number}
      >();
      if (dailyTasks) {
        Object.keys(dailyTasks).forEach(key => {
          const oneDailyTasks = dailyTasks[key as any];
          const processedTasks = {
            depositTask: 0,
            betTask: 0,
          };
          if (Array.isArray(oneDailyTasks)) {
            oneDailyTasks.forEach(task => {
              if (task.taskType === 1) {
                processedTasks.depositTask = task.targetAmount || 0;
              } else if (task.taskType === 2) {
                processedTasks.betTask = task.targetAmount || 0;
              }
            });
            nextDailyTasksMap.set(Number(key), processedTasks);
          }
        });
      }

      // 创建签到记录映射，便于查找
      const weekRecordsMap = new Map<number, UserDailySignRecordVO>();
      weekRecords.forEach(record => {
        if (record.dayOfWeek) {
          weekRecordsMap.set(record.dayOfWeek, record);
        }
      });

      // 生成渲染数据
      const result: RenderDayData[] = [];
      for (let dayOfWeek = 1; dayOfWeek <= 7; dayOfWeek++) {
        const dailyReward = dailyRewards.find(
          reward => reward.dayOfWeek === dayOfWeek,
        );
        const weekRecord = weekRecordsMap.get(dayOfWeek);

        // 判断签到状态
        const isChecked = !!weekRecord; // 是否已签到
        const isReceived = weekRecord?.receiveStatus === 1; // 奖励是否已领取（1-已领取，0-未领取）
        const isTaskCompleted = weekRecord?.arriveStatus === 1; // 任务是否已完成（1-已完成，0-未完成）

        // 计算状态
        const status = (() => {
          // 未来的日期 -> 锁定
          if (dayOfWeek > currentWeekDay) {
            return CheckInStatus.LOCKED;
          }

          // 过去的日期
          if (dayOfWeek < currentWeekDay) {
            // 已签到且已领取奖励 -> 完成，否则 -> 过期
            return isChecked && isReceived
              ? CheckInStatus.COMPLETED
              : CheckInStatus.EXPIRED;
          }

          // 当天
          if (!isChecked) {
            // 未签到 -> 可签到
            return CheckInStatus.AVAILABLE;
          }

          // 已签到
          if (isReceived) {
            // 已领取奖励 -> 完成
            return CheckInStatus.COMPLETED;
          }

          // 未领取奖励
          // 任务已完成 -> 可领取奖励，任务未完成 -> 需完成任务
          return isTaskCompleted
            ? CheckInStatus.TASK_COMPLETED
            : CheckInStatus.CHECKED_IN;
        })();

        result.push({
          day: WEEK_DAY_MAP[dayOfWeek as keyof typeof WEEK_DAY_MAP],
          reward: String(dailyReward?.rewardAmount || 0),
          icon: getRewardIcon(dayOfWeek),
          isChecked,
          dayOfWeek,
          rewardAmount: dailyReward?.rewardAmount || 0,
          arriveStatus: weekRecord?.arriveStatus,
          receiveStatus: weekRecord?.receiveStatus,
          status,
        });
      }

      return {renderList: result, dailyTasksMap: nextDailyTasksMap};
    },
    [currentWeekDay, getRewardIcon],
  );

  // 处理后的渲染数据
  const renderList = useMemo(() => {
    if (!rawData) {
      return createEmptyData();
    }
    const {
      renderList: processedRenderList,
      dailyTasksMap: processedDailyTasksMap,
    } = processCheckInData(rawData);
    setCheckInDailyTasksMap(processedDailyTasksMap);
    return processedRenderList;
  }, [rawData, createEmptyData, processCheckInData]);

  // 获取指定状态的数量
  const getStatusCount = useCallback(
    (status: CheckInStatus): number => {
      return renderList.filter(item => item.status === status).length;
    },
    [renderList],
  );

  // 当天是否已签到
  const isTodayChecked = useMemo(() => {
    const todayItem = renderList.find(
      item => item.dayOfWeek === currentWeekDay,
    );
    return todayItem?.isChecked || false;
  }, [renderList, currentWeekDay]);

  // 额外奖励是否已领取
  const extraRewardReceived = useMemo(() => {
    return rawData?.extraRewardReceived || false;
  }, [rawData]);

  // 当天的任务数据
  const currentDayTasks = useMemo(() => {
    const tasks = checkInDailyTasksMap.get(currentWeekDay);
    if (!tasks) {
      return undefined;
    }
    return {
      ...tasks,
      depositTaskCompleted: rawData?.dailyRechargeAmount || 0,
      betTaskCompleted: rawData?.dailyBettingAmount || 0,
    };
  }, [checkInDailyTasksMap, currentWeekDay, rawData]);

  // 获取签到数据
  const fetchCheckInData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await checkInService.getCheckDetail();

      // 检查接口返回的数据是否有效
      if (!result || (!result.dailyRewards && !result.weekRecords)) {
        setRawData(null);
        return;
      }

      setRawData(result);
    } catch (error) {
      // 设置默认数据以防接口失败
      const defaultData: ActivityDetailVO = {
        dailyRewards: Array.from({length: 7}, (_, i) => ({
          dayOfWeek: i + 1,
          rewardAmount: 0, // 递增的奖励金额
          sort: i + 1,
        })),
        currentVipBonusRate: 0,
        extraRewardReceived: false,
        currentVipLevel: 0,
        weekRecords: [],
        signedDays: 0,
        indiaDayOfWeek: 1,
        currentDate: Date.now(),
        dailyBettingAmount: 0,
        dailyRechargeAmount: 0,
      };
      setRawData(defaultData);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 签到方法
  const checkIn = useCallback(async () => {
    if (isTodayChecked) {
      return Promise.reject(); // 如果当天已签到，不执行签到
    }

    setIsCheckingIn(true);
    try {
      const res = await checkInService.signIn();
      // 刷新签到数据
      await fetchCheckInData();
      return res;
    } catch (error) {
      throw error; // 抛出错误让调用方处理
    } finally {
      setIsCheckingIn(false);
    }
  }, [isTodayChecked, fetchCheckInData]);

  // 通用的领取奖励方法
  const handleReceiveReward = useCallback(
    async (
      rewardType: 'daily' | 'extra',
      validateFn?: () => boolean,
    ): Promise<ReceiveRewardVO> => {
      // 如果提供了验证函数，执行验证
      if (validateFn && !validateFn()) {
        throw new Error('当前无法领取奖励');
      }

      const setLoadingState =
        rewardType === 'daily'
          ? setIsReceivingReward
          : setIsReceivingExtraReward;
      const serviceMethod =
        rewardType === 'daily'
          ? checkInService.receiveReward
          : checkInService.receiveExtraReward;
      setLoadingState(true);
      try {
        const result: ReceiveRewardVO = await serviceMethod();
        // 刷新签到数据
        await fetchCheckInData();

        return result;
      } catch (error) {
        throw error; // 抛出错误让调用方处理
      } finally {
        setLoadingState(false);
      }
    },
    [fetchCheckInData],
  );

  // 领取每日签到奖励方法
  const receiveReward = useCallback(async (): Promise<ReceiveRewardVO> => {
    return handleReceiveReward('daily');
  }, [handleReceiveReward]);

  // 领取额外奖励（宝箱）方法
  const receiveExtraReward = useCallback(async (): Promise<ReceiveRewardVO> => {
    // 可以根据需要添加验证逻辑，比如检查是否完成7天签到
    // const validateExtraReward = () => {
    //   return getStatusCount(CheckInStatus.COMPLETED) >= 7;
    // };

    return handleReceiveReward('extra');
  }, [handleReceiveReward]);

  // 刷新数据方法
  const refreshData = useCallback(async () => {
    await fetchCheckInData();
  }, [fetchCheckInData]);

  // 更新特定天的签到状态
  const updateDayStatus = useCallback(
    (dayOfWeek: number, checked: boolean) => {
      if (!rawData) {
        return;
      }

      const updatedWeekRecords = [...(rawData.weekRecords || [])];
      const existingIndex = updatedWeekRecords.findIndex(
        record => record.dayOfWeek === dayOfWeek,
      );

      if (existingIndex >= 0) {
        updatedWeekRecords[existingIndex] = {
          ...updatedWeekRecords[existingIndex],
          arriveStatus: checked ? 1 : 0,
        };
      } else {
        updatedWeekRecords.push({
          dayOfWeek,
          arriveStatus: checked ? 1 : 0,
          receiveStatus: 0,
          userId: 0,
          activityConfigId: 0,
          rewardType: 1,
          signDate: new Date().toISOString().split('T')[0],
          weekCode: 0,
        });
      }

      setRawData({
        ...rawData,
        weekRecords: updatedWeekRecords,
      });
    },
    [rawData],
  );

  // 初始化数据
  useEffect(() => {
    fetchCheckInData();
  }, [fetchCheckInData]);

  return {
    renderList,
    rawData,
    currentWeekDay,
    isLoading,
    isCheckingIn,
    isReceivingReward,
    isReceivingExtraReward,
    isTodayChecked,
    extraRewardReceived,
    dailyTasksMap: checkInDailyTasksMap,
    currentDayTasks,
    checkIn,
    receiveReward,
    receiveExtraReward,
    refreshData,
    updateDayStatus,
    getStatusCount,
  };
}
